import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import set from 'lodash/set';
import uniq from 'lodash/uniq';

import {
  FILTER_PERIOD,
  FILTER_PAY_TO,
  FILTER_APPLICATIONS,
  FILTER_NETWORKS,
  FILTER_CLUSTERS,
  FILTER_AFFILIATES,
  FILTER_COUNTRIES,
  FILTER_OWNERS,
  FILTER_LABELS,
  FILTER_PLATFORMS,
  FILTER_CLIENTS,
  FILTER_TRAFFIC_TYPE,
  FILTER_TRAFFIC_TYPE_ADVERTISING,
} from 'scenes/Trackers/Tracker/constants';
import { columnsSchema, chartSchema } from 'scenes/Trackers/Tracker/schemas';

export const clean = source => {
  return Object.keys(source).reduce((m, k) => {
    const v = source[k];
    if (v !== undefined && !(Array.isArray(v) && !v.length)) {
      return {
        ...m,
        [k]: v,
      };
    }

    return m;
  }, {});
};

const getPeriodFilters = ({ fromDate, payToDate, toDate }) =>
  clean({
    [FILTER_PERIOD]: [fromDate, toDate],
    [FILTER_PAY_TO]: payToDate,
  });

const getMetricFilters = ({
  affiliates,
  applications,
  clients,
  clusters,
  countries,
  labels,
  networks,
  owners,
  platforms,
}) =>
  clean({
    [FILTER_AFFILIATES]: affiliates,
    [FILTER_APPLICATIONS]: applications,
    [FILTER_CLIENTS]: clients,
    [FILTER_CLUSTERS]: clusters,
    [FILTER_COUNTRIES]: countries,
    [FILTER_LABELS]: labels,
    [FILTER_NETWORKS]: networks,
    [FILTER_OWNERS]: owners,
    [FILTER_PLATFORMS]: platforms,
    [FILTER_TRAFFIC_TYPE]: FILTER_TRAFFIC_TYPE_ADVERTISING,
  });

const isNumeric = ({ metricValueType }) =>
  ['Number', 'Currency', 'Percent'].includes(metricValueType);
const getId = ({ id }) => id;

export const resolveLogDimensions = (data, dimensions) => {
  const dataClone = cloneDeep(data);

  ['target.dimension', 'target.secondaryDimension'].forEach(path => {
    const dimensionId = get(dataClone, path);
    if (dimensionId) {
      const dimension = dimensions.find(({ id }) => id === dimensionId);
      if (!dimension) {
        throw new Error(`Unknown dimension ${dimensionId}`);
      }
      set(dataClone, path, dimension.trackerId);
    }
  });

  return dataClone;
};

export const getTrackerParamsFromRule = ({
  id,
  dimension,
  ruleModel,
  realPeriod,
}) => {
  const drillPath = [
    {
      id,
      type: dimension,
    },
  ];

  const filtersCollection = [
    {
      ...getPeriodFilters(realPeriod.period),
      ...getMetricFilters(ruleModel.dynamicTarget || ruleModel.target),
    },
  ];

  const _metrics = uniq(
    ruleModel.conditions.filter(isNumeric).map(({ metric }) => metric)
  );
  const columns = {
    ...columnsSchema.default(),
  };

  columns.order = uniq([..._metrics, ...columns.order]);

  const chart = {
    ...chartSchema.default(),
  };

  chart.metrics = _metrics;

  return {
    drillPath,
    filtersCollection,
    columns,
    charts: [chart],
    ready: true,
  };
};

export const getTrackerParams = data => {
  const { baseMetrics = [], compareMetrics = [] } = groupBy(
    data.metrics,
    ({ isCompared }) => (isCompared ? 'compareMetrics' : 'baseMetrics')
  );
  const drillPath = [
    {
      id: data.affectedDimension,
      type: data.target.dimension,
    },
  ];

  if (data.affectedSecondaryDimension) {
    drillPath.push({
      id: data.affectedSecondaryDimension,
      type: data.target.secondaryDimension,
    });
  }

  const filtersCollection = [
    {
      ...(data.targetDataPeriod
        ? getPeriodFilters(data.targetDataPeriod.period)
        : null),
      ...getMetricFilters(data.target),
    },
  ];

  const columns = {
    ...columnsSchema.default(),
  };

  columns.order = uniq([
    ...(data.metrics?.map(getId) || []),
    ...(data.actionMetrics?.map(getId) || []),
    ...columns.order,
  ]);

  const chart = {
    ...chartSchema.default(),
  };

  const statuses = []; // = data.target.statuses;

  if (compareMetrics.length > 0) {
    filtersCollection.unshift({
      ...getPeriodFilters(data.targetDataPeriod.comparePeriod),
      ...getMetricFilters(data.target),
    });

    chart.metrics = uniq(compareMetrics.map(getId));
  } else if (baseMetrics.length > 0) {
    chart.metrics = uniq(baseMetrics.filter(isNumeric).map(getId));
  } else {
    chart.metrics = ['RegActions'];
  }

  return clean({
    drillPath: drillPath,
    statuses: statuses,
    filtersCollection: filtersCollection,
    columns: columns,
    charts: [chart],
    ready: true,
  });
};
